/**
 * Hook del SSE PÚBLICO (`/api/v1/public/stream`, sin token).
 *
 * Se conecta por `fetch` + `ReadableStream` en vez de `EventSource` porque el endpoint
 * no puede llevar header de Authorization; si el navegador no soporta streaming o la
 * conexión falla 2 veces seguidas, cae a polling de 5 s.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { FightMetrics, StreamEvent } from '@thepit/contracts';
import { API_URL } from './api.js';

export type StreamStatus = 'connecting' | 'live' | 'polling' | 'offline';

interface Options {
  onEvent: (event: StreamEvent) => void;
  /** Se llama cuando hay que re-sincronizar (reconexión o polling). */
  onPoll: () => void;
  enabled: boolean;
}

export function usePublicStream({ onEvent, onPoll, enabled }: Options): { status: StreamStatus } {
  const [status, setStatus] = useState<StreamStatus>('connecting');
  const abortRef = useRef<AbortController | null>(null);
  // Los callbacks van en refs para no reconectar en cada render.
  const onEventRef = useRef(onEvent);
  const onPollRef = useRef(onPoll);
  onEventRef.current = onEvent;
  onPollRef.current = onPoll;

  useEffect(() => {
    if (!enabled) return;

    let disposed = false;
    let failures = 0;
    let pollTimer: number | undefined;
    let reconnect: number | undefined;

    const poll = (): void => onPollRef.current();

    const connect = async (): Promise<void> => {
      if (disposed) return;
      abortRef.current = new AbortController();
      setStatus('connecting');
      try {
        const res = await fetch(`${API_URL}/api/v1/public/stream`, {
          headers: { accept: 'text/event-stream' },
          signal: abortRef.current.signal,
        });
        if (!res.ok || !res.body) throw new Error(`sse ${res.status}`);
        setStatus('live');
        failures = 0;

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        // `stream.end` = el servidor decidió cortar (no hay pelea viva, o se cumplió el
        // tope de duración). Reconectar contra un stream vacío mantiene despiertas la
        // máquina de Fly y la computa de Neon sin motivo: solo se reconecta si la pelea
        // sigue viva. (Directriz de costos ADR-0011.)
        let endReason: 'no-live-fight' | 'max-duration' | null = null;

        for (;;) {
          const { value, done } = await reader.read();
          if (done || disposed) break;
          buffer += decoder.decode(value, { stream: true });

          let cut = buffer.indexOf('\n\n');
          while (cut !== -1) {
            const frame = buffer.slice(0, cut);
            buffer = buffer.slice(cut + 2);
            cut = buffer.indexOf('\n\n');

            let payload = '';
            for (const line of frame.split('\n')) {
              if (line.startsWith('data:')) payload += line.slice(5).trim();
            }
            if (!payload) continue;
            try {
              const parsed = JSON.parse(payload) as StreamEvent;
              if (parsed.type === 'stream.end') {
                endReason = parsed.reason;
                break;
              }
              onEventRef.current(parsed);
            } catch {
              /* frame inválido: se ignora */
            }
          }
          if (endReason) break;
        }

        if (endReason && !disposed) {
          // Re-sincroniza una vez para que la vista pase a "sin pelea" y no quede
          // mostrando métricas congeladas de un evento que ya terminó.
          onPollRef.current();
          if (endReason === 'max-duration') {
            // La pelea sigue viva: se retoma el stream (una vez por tope, no en bucle).
            reconnect = window.setTimeout(() => void connect(), 500);
          }
          setStatus(navigator.onLine === false ? 'offline' : 'polling');
          return;
        }
        throw new Error('cerrado');
      } catch (error) {
        if (disposed || (error as Error).name === 'AbortError') return;
        failures += 1;
        if (failures >= 2) {
          // Degradado pero funcional: seguimos actualizando por polling. No es
          // "sin señal" — el badge lo distingue para no mentirle al operador.
          setStatus(navigator.onLine === false ? 'offline' : 'polling');
          pollTimer = window.setInterval(poll, 5_000);
          return;
        }
        reconnect = window.setTimeout(() => void connect(), 1_000 * failures);
      }
    };

    void connect();

    const onOnline = (): void => {
      failures = 0;
      if (pollTimer) window.clearInterval(pollTimer);
      void connect();
    };
    window.addEventListener('online', onOnline);

    return () => {
      disposed = true;
      window.removeEventListener('online', onOnline);
      if (pollTimer) window.clearInterval(pollTimer);
      if (reconnect) window.clearTimeout(reconnect);
      abortRef.current?.abort();
    };
  }, [enabled]);

  return { status };
}

/** Handler típico: métricas + últimos tickets. */
export function useStreamState(): {
  metrics: FightMetrics | null;
  setMetrics: (m: FightMetrics | null) => void;
  onEvent: (event: StreamEvent) => void;
  onPoll: () => void;
} {
  const [metrics, setMetrics] = useState<FightMetrics | null>(null);

  const onEvent = useCallback((event: StreamEvent) => {
    if (event.type === 'pool.updated') setMetrics(event.metrics);
  }, []);

  const onPoll = useCallback(() => {
    // El polling real lo hace React Query al invalidar; acá solo marcamos.
  }, []);

  return { metrics, setMetrics, onEvent, onPoll };
}
